import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchThemeData } from '../../admin/themeSlice';
import { selectAdminToken } from '../../admin/sessionSlice';
import { setBackButtonUrl } from '../uiSlice';
import { fetchReport } from '../../functions/fetchReport';
import { initStageReport, updateStageProgress } from '../../functions/stageReport';
import { updateIntroProgress, parseIntroComplete, getStageAnswerPlayed } from '../../functions/futureProgress';
import { extractReportIdFromResponse, normalizeReportTimeValue, resolveReportsAuthToken, sendReport } from '../../functions/sendReport';
import { mergeReportId } from '../loginSlice';
import IntroPage, { IntroMapPage } from './IntroPage';
import Stage1Poll from './Stage1Poll';
import Stage2Planets from './Stage2Planets';
import Stage3Economy from './Stage3Economy';
import Stage4Maps from './Stage4Maps';
import Stage4Graph from './Stage4Graph';
import Stage5Statement from './Stage5Statement';
import Stage6Jigsaw from './Stage6Jigsaw';
import Stage7School from './Stage7School';
import FutureThankYou from './FutureThankYou';
import './future.css';

function resolveFlowStep(reportRow) {
  if (!parseIntroComplete(reportRow)) return 'intro';
  for (let i = 1; i <= 7; i++) {
    if (!getStageAnswerPlayed(reportRow, i)) return `stage_${i}`;
  }
  return 'thankyou';
}

function FutureGame() {
  const dispatch = useDispatch();
  const { data: themeData, status } = useSelector((state) => state.theme);
  const authUser = useSelector((state) => state.auth.user);
  const adminToken = useSelector(selectAdminToken);

  const [reportData, setReportData] = useState(null);
  const [flowStep, setFlowStep] = useState(null);
  const [subStep, setSubStep] = useState('intro');
  const [loading, setLoading] = useState(true);
  const [stageStartedAt, setStageStartedAt] = useState(null);
  const [graphRanking, setGraphRanking] = useState(null);
  // Fresh theme fetch for this mount — ignore login-cached Redux until it completes.
  const [themeReady, setThemeReady] = useState(false);
  const themeFetchStartedRef = useRef(false);

  const intro = themeData?.intro || {};
  const thankyou = themeData?.thankyou || {};
  const pagesBg = themeData?.pages_bg || {};
  const stagesData = themeData?.stages_data || {};
  // Intro no longer has its own desktop/mobile images in admin — falls back
  // to the site's main theme background (theme0.background_desk/_mob).
  const introThemeBg = {
    background_desk: themeData?.background_desk,
    background_mob: themeData?.background_mob,
  };

  useEffect(() => {
    dispatch(setBackButtonUrl('/login?&save=true'));
  }, [dispatch]);

  useEffect(() => {
    const body = document.body;
    const prevImage = body.style.backgroundImage;
    const prevColor = body.style.backgroundColor;
    body.style.backgroundImage = 'none';
    body.style.backgroundColor = 'transparent';
    return () => {
      body.style.backgroundImage = prevImage;
      body.style.backgroundColor = prevColor;
    };
  }, []);

  // Always refetch on mount so admin saves after login are picked up.
  // Previously only fetched when status === 'idle', so a successful login
  // fetch left the game stuck on stale Redux theme data.
  useEffect(() => {
    if (!adminToken) {
      setLoading(false);
      setThemeReady(false);
      return;
    }
    if (themeFetchStartedRef.current) return;
    themeFetchStartedRef.current = true;
    setThemeReady(false);
    setLoading(true);
    dispatch(fetchThemeData({ themeId: null }))
      .finally(() => {
        setThemeReady(true);
      });
  }, [dispatch, adminToken]);

  const loadReport = useCallback(async () => {
    if (!authUser?.userId || !adminToken) return null;
    try {
      const resp = await fetchReport({ userId: authUser.userId, token: adminToken });
      const row = resp?.report || null;
      setReportData(row);
      return row;
    } catch {
      return null;
    }
  }, [authUser?.userId, adminToken]);

  useEffect(() => {
    void (async () => {
      if (!adminToken || !authUser?.userId) {
        setLoading(false);
        return;
      }
      if (!themeReady) return;
      if (status !== 'succeeded' && status !== 'failed') return;
      let row = await loadReport();
      if (!row) {
        try {
          await initStageReport({
            token: adminToken,
            userId: authUser.userId,
            email: authUser.email,
            name: authUser.name,
            userType: authUser.role,
            reportId: authUser.reportId,
            themeName: themeData?.themeName || themeData?.themename || 'Future India',
          });
          row = await loadReport();
        } catch (e) {
          console.error('initStageReport failed:', e);
        }
      }
      const step = resolveFlowStep(row);
      setFlowStep(step);
      if (step === 'intro') setSubStep('intro');
      setLoading(false);
    })();
  }, [adminToken, authUser, status, themeReady, loadReport, themeData?.themeName, themeData?.themename]);

  const completeStage = async (stageNum, payload = {}) => {
    if (!authUser?.userId || !adminToken) return;
    const elapsed = Math.max(0, Math.floor((Date.now() - Number(stageStartedAt || Date.now())) / 1000));
    const points = Math.max(0, Math.floor(Number(themeData?.correct_points) || 10));
    try {
      await updateStageProgress({
        token: adminToken,
        userId: authUser.userId,
        dayNumber: stageNum,
        points,
        timeSeconds: elapsed,
        answer: JSON.stringify(payload),
        allCorrect: true,
        email: authUser.email,
        name: authUser.name,
      });
      await loadReport();
    } catch (e) {
      console.warn('stage complete failed', e);
    } finally {
      // Always advance locally so a failed/slow save can't strand the player
      // on the same stage (the save is still attempted above for scoring).
      setFlowStep(stageNum >= 7 ? 'thankyou' : `stage_${stageNum + 1}`);
      setStageStartedAt(Date.now());
    }
  };

  const handleIntroMapDone = async (countries) => {
    try {
      await updateIntroProgress({
        token: adminToken,
        userId: authUser.userId,
        introComplete: true,
        countries,
      });
      await loadReport();
      setSubStep('done');
      setFlowStep('stage_1');
      setStageStartedAt(Date.now());
    } catch (e) {
      console.warn('intro progress failed', e);
      setFlowStep('stage_1');
      setStageStartedAt(Date.now());
    }
  };

  useEffect(() => {
    if (flowStep && flowStep.startsWith('stage_') && !stageStartedAt) {
      setStageStartedAt(Date.now());
    }
  }, [flowStep, stageStartedAt]);

  if (loading || !themeReady || status === 'loading' || status === 'idle') {
    return (
      <div className="fi-game-shell">
        <div className="fi-loader">
          <div className="fi-loader-spinner" />
        </div>
      </div>
    );
  }

  if (flowStep === 'thankyou') {
    return (
      <FutureThankYou
        thankyou={thankyou}
        pagesBg={pagesBg}
        reportData={reportData}
        stagesData={stagesData}
      />
    );
  }

  if (flowStep === 'intro') {
    if (subStep === 'intro') {
      return (
        <div className="fi-game-shell">
          <IntroPage
            intro={intro}
            pagesBg={pagesBg}
            themeBg={introThemeBg}
            onComplete={() => setSubStep('map')}
          />
        </div>
      );
    }
    return (
      <div className="fi-game-shell">
        <IntroMapPage
          intro={intro}
          pagesBg={pagesBg}
          onComplete={handleIntroMapDone}
        />
      </div>
    );
  }

  const stageMap = {
    stage_1: <Stage1Poll stage={stagesData.stage_1} pagesBg={pagesBg} onComplete={(p) => completeStage(1, p)} />,
    stage_2: <Stage2Planets stage={stagesData.stage_2} pagesBg={pagesBg} onComplete={(p) => completeStage(2, p)} />,
    stage_3: <Stage3Economy stage={stagesData.stage_3} pagesBg={pagesBg} onComplete={(p) => completeStage(3, p)} />,
    stage_4: <Stage4Maps stage={stagesData.stage_4} pagesBg={pagesBg} onComplete={(p) => { setGraphRanking(p.ranking || []); completeStage(4, p); }} />,
    stage_5: <Stage5Statement stage={stagesData.stage_5} pagesBg={pagesBg} onComplete={(p) => completeStage(5, p)} />,
    stage_6: <Stage6Jigsaw stage={stagesData.stage_6} pagesBg={pagesBg} onComplete={(p) => completeStage(6, p)} />,
    stage_7: <Stage7School stage={stagesData.stage_7} pagesBg={pagesBg} adminToken={adminToken} userId={authUser?.userId} onComplete={(p) => completeStage(7, p)} />,
  };

  if (graphRanking !== null) {
    return (
      <div className="fi-game-shell">
        <Stage4Graph
          ranking={graphRanking}
          stagesData={stagesData}
          pagesBg={pagesBg}
          onNext={() => setGraphRanking(null)}
        />
      </div>
    );
  }

  return (
    <div className="fi-game-shell">
      {stageMap[flowStep] || (
        <div className="fi-stage-root">
          <p className="fi-heading">Loading stage…</p>
        </div>
      )}
    </div>
  );
}

export default FutureGame;
